import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { useAuth } from "../context/auth-context";
import { markCompanySelected } from "@/lib/api";
import { Building2, Loader2, AlertCircle, Shield } from "lucide-react";
import { useUserCompanies } from "@/hooks/use-user-companies";

interface CompanySelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redirectTo?: string;
}

export const CompanySelectorModal: React.FC<CompanySelectorModalProps> = ({
  open,
  onOpenChange,
  redirectTo = "/"
}) => {
  const navigate = useNavigate();
  const { setSelectedCompanyId } = useAuth();
  const { companies, isLoading: loading, isError } = useUserCompanies();
  const [selected, setSelected] = useState<string | null>(null);

  const error = isError ? "No fue posible cargar las sociedades" : null;

  // Si solo hay una sociedad, preseleccionarla
  useEffect(() => {
    if (open && companies.length === 1 && !selected) {
      setSelected(companies[0].id);
    }
  }, [open, companies, selected]);

  const handleConfirm = async () => {
    if (selected) {
      setSelectedCompanyId(selected);
      try {
        localStorage.setItem("selectedCompanyId", selected);
        markCompanySelected();
      } catch (error) {
        console.error("Error guardando sociedad en localStorage:", error);
      }

      onOpenChange(false);

      const maxWaitTime = 500;
      const checkInterval = 50;
      let waited = 0;

      while (waited < maxWaitTime) {
        const currentToken = localStorage.getItem("token");
        const currentCompanyId = localStorage.getItem("selectedCompanyId");
        if (currentToken && currentCompanyId === selected) break;
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
        waited += checkInterval;
      }

      if (!localStorage.getItem("token")) return;

      navigate(redirectTo, { replace: true });
    }
  };

  const canConfirm = Boolean(selected);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-lg mx-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Selecciona una sociedad
          </DialogTitle>
          <DialogDescription className="text-sm pt-1">
            Elige la sociedad con la que deseas trabajar en esta sesión.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
              <span className="text-sm text-muted-foreground">
                Cargando sociedades...
              </span>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-destructive/10 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            </div>
          )}

          {!loading && !error && companies.length === 0 && (
            <div className="text-center py-8">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
              <p className="text-sm font-medium text-muted-foreground mb-1">
                No tienes acceso a ninguna sociedad
              </p>
              <p className="text-xs text-muted-foreground">
                Contacta al administrador para obtener acceso
              </p>
            </div>
          )}

          {!loading && !error && companies.length > 0 && (
            <Field>
              <FieldLabel htmlFor="company-select">Sociedad</FieldLabel>
              <Select
                value={selected ?? ""}
                onValueChange={(value) => setSelected(value || null)}
              >
                <SelectTrigger id="company-select" className="w-full">
                  <SelectValue placeholder="Selecciona una sociedad..." />
                </SelectTrigger>
                <SelectContent>
                  {companies.some((c) => c.id === "TENANT") && (
                    <>
                      <div className="relative py-2">
                        <div className="absolute inset-0 flex items-center px-2">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">
                            Tenant
                          </span>
                        </div>
                      </div>
                      {companies
                        .filter((c) => c.id === "TENANT")
                        .map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4 text-amber-500 flex-shrink-0" />
                              <span>{company.name}</span>
                              <span className="text-xs text-muted-foreground ml-auto">
                                Administrar datos del tenant
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                    </>
                  )}
                  {companies.some((c) => c.id !== "TENANT") && (
                    <>
                      <div className="relative py-2">
                        <div className="absolute inset-0 flex items-center px-2">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">
                            Compañías
                          </span>
                        </div>
                      </div>
                      {companies
                        .filter((c) => c.id !== "TENANT")
                        .map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            <div className="flex items-center gap-2">
                              <span
                                className={`h-2.5 w-2.5 rounded-full flex-shrink-0 animate-pulse ${
                                  company.active !== false
                                    ? 'bg-green-500'
                                    : 'bg-red-500'
                                }`}
                              />
                              <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span>{company.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>

        {!loading && !error && companies.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="w-full sm:w-auto"
            >
              Continuar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
